import { useEffect, useState } from "react";
import modalStyle from "../styles/modal.module.scss";
import materialSupportNotes from "../data/materialSupportNotes.json";
import { formatBrandMaterialName } from "../utilities/brandData";
import {
  closeIcon,
  downloadIcon,
  editIcon,
  starFillIcon,
  starOutlineIcon,
} from "./Icons";

function MaterialDetailLayout({
  materialData,
  ROOT_SERVER,
  viewing,
  imageNumber,
  onChangeImage,
  imageLoaded,
  onImageLoaded,
  onClose,
  closeLabel = "close",
  onEdit,
  canEdit,
  editLabel,
  editMeta,
  onPrimaryAction,
  primaryLabel,
  onCollectionAction,
  collectionLabel,
  collectionActive,
  onSecondaryAction,
  secondaryLabel,
  secondaryLoadingLabel,
  secondaryIsLoading,
  brandData,
  showBrandData,
  mode = "modal",
  containerRef,
  onContainerClick,
  imagePaneStyle,
  dataPaneStyle,
}) {
  const hasBrandData =
    Boolean(showBrandData) &&
    Array.isArray(brandData) &&
    brandData.length > 0;
  const [infoPanel, setInfoPanel] = useState(
    hasBrandData ? "brand" : "sustainability",
  );

  useEffect(() => {
    setInfoPanel(hasBrandData ? "brand" : "sustainability");
  }, [hasBrandData, materialData?.id]);

  if (!materialData) {
    return null;
  }

  const {
    title,
    slug,
    description,
    price,
    status,
    type,
    subtype,
    images,
    substrate,
    vendor,
    character,
    feature,
    view_count,
  } = materialData;

  const materialImages = Array.isArray(images) ? images : [];
  const characterList = Array.isArray(character) ? character : [];
  const featureList = Array.isArray(feature) ? feature : [];
  const isPageMode = mode === "page";
  const activeInfoPanel =
    hasBrandData && infoPanel === "brand" ? "brand" : "sustainability";

  const renderSupportPanel = () => {
    const normalizedType = String(type || "").toLowerCase();
    const normalizedVendor = String(vendor || "").toLowerCase();

    const infoSource =
      normalizedType === "split" && normalizedVendor === "tectuff"
        ? materialSupportNotes.split_tectuff
        : normalizedType === "split"
          ? materialSupportNotes.split
          : materialSupportNotes.default;

    const safeInfo =
      infoSource && Array.isArray(infoSource.info)
        ? infoSource.info
        : materialSupportNotes.default.info;
    const safeTitle =
      infoSource && typeof infoSource.title === "string"
        ? infoSource.title
        : materialSupportNotes.default.title;

    return (
      <div className={modalStyle.infoBox}>
        <div style={{ width: "100%" }} className={modalStyle.infoContent}>
          {hasBrandData ? (
            <div
              className={modalStyle.infoPanelTabs}
              role="tablist"
              aria-label="Material notes"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeInfoPanel === "brand"}
                className={`${modalStyle.infoPanelTab} ${
                  activeInfoPanel === "brand" ? modalStyle.infoPanelTabActive : ""
                }`}
                onClick={() => setInfoPanel("brand")}
              >
                Brand Info
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeInfoPanel === "sustainability"}
                className={`${modalStyle.infoPanelTab} ${
                  activeInfoPanel === "sustainability"
                    ? modalStyle.infoPanelTabActive
                    : ""
                }`}
                onClick={() => setInfoPanel("sustainability")}
              >
                {safeTitle}
              </button>
            </div>
          ) : (
            <div className={modalStyle.infoContentHeader}>
              <h4>{safeTitle}</h4>
            </div>
          )}
          <div
            className={`${modalStyle.infoContentBody} ${
              activeInfoPanel === "brand" ? modalStyle.infoBrandBody : ""
            }`}
          >
            {activeInfoPanel === "brand" ? (
              <ul className={modalStyle.infoBrandList}>
                {brandData.map((data, i) => (
                  <li key={`${data.pcx_material_number || "pcx"}-${i}`}>
                    <span className={modalStyle.infoBrandName}>
                      {formatBrandMaterialName(data, title)}
                    </span>
                    <span className={modalStyle.infoBrandMeta}>
                      {[
                        data.thickness != null && data.thickness !== ""
                          ? `${data.thickness}mm`
                          : null,
                        data.pcx_material_number != null
                          ? `PCX# ${data.pcx_material_number}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className={modalStyle.infoBulletList}>
                {safeInfo.map((item) => (
                  <li className={modalStyle.infoBulletItem} key={item}>
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${modalStyle.materialModal} ${
        isPageMode ? modalStyle.pageMaterialModal : ""
      }`}
      onClick={onContainerClick}
      ref={containerRef}
    >
      <div
        className={`${modalStyle.modalContent} ${
          isPageMode ? modalStyle.pageModalContent : ""
        }`}
      >
        <div
          className={`${modalStyle.imageContainer} ${
            isPageMode ? modalStyle.pageImageContainer : ""
          }`}
          style={imagePaneStyle}
        >
          {!imageLoaded && (
            <div className={modalStyle.imageStateOverlay} aria-hidden="true">
              <div className={modalStyle.imageLoaderDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <img
            className={`${modalStyle.materialImage} ${
              imageLoaded ? modalStyle.loadedImage : modalStyle.pendingImage
            }`}
            src={viewing}
            alt={`${vendor} - ${title}`}
            onLoad={onImageLoaded}
          />
          <div
            className={`${modalStyle.imageSelector} ${
              isPageMode ? modalStyle.pageImageSelector : ""
            }`}
          >
            {materialImages.length >= 2 &&
              materialImages.map((image, i) => (
                <div
                  key={`${image} -- ${i}`}
                  data-image={`${image} -- ${i}`}
                  className={`${modalStyle.swatch} ${
                    i === imageNumber ? modalStyle.active : ""
                  }`}
                  style={{ overflow: "hidden" }}
                  onClick={() => onChangeImage(i)}
                >
                  <img
                    src={`${ROOT_SERVER}${vendor}/${slug}/thumbnail/${image}`}
                    alt={slug + i}
                  />
                </div>
              ))}
          </div>
        </div>

        <div
          className={`${modalStyle.dataContainer} ${
            isPageMode ? modalStyle.pageDataContainer : ""
          }`}
          style={dataPaneStyle}
        >
          {!isPageMode && (
            <div className={modalStyle.closeButton} onClick={onClose}>
              <span>{closeLabel}</span> {closeIcon}
            </div>
          )}
          <div
            className={`${modalStyle.dataContainerTop} ${
              isPageMode ? modalStyle.pageDataContainerTop : ""
            }`}
          >
            <h3 className={modalStyle.vendorLabel}>{vendor}</h3>
            <h1 className={modalStyle.materialTitle}>{title}</h1>
            <h3 className={modalStyle.materialDescription}>{description}</h3>

            <div className={modalStyle.modalMetaGrid}>
              <div className={modalStyle.modalMetaCard}>
                <span>Substrate</span>
                <strong>{substrate || "--"}</strong>
              </div>
              <div className={modalStyle.modalMetaCard}>
                <span>Type</span>
                <strong>{type || "--"}</strong>
              </div>
              <div className={modalStyle.modalMetaCard}>
                <span>Group</span>
                <strong>{subtype || "--"}</strong>
              </div>
            </div>

            <div className={modalStyle.modalPriceBlock}>
              <div className={modalStyle.modalPriceStatusRow}>
                <div className={modalStyle.modalPriceSide}>
                  <div className={modalStyle.modalPriceRow}>
                    <span className={modalStyle.modalPriceValue}>
                      ${Number(price || 0).toFixed(2)}
                    </span>
                    <span className={modalStyle.modalPriceUom}>/ sq.ft.</span>
                  </div>
                </div>
                <div className={modalStyle.modalStatusSide}>
                  <span className={modalStyle.modalStatusLabel}>Status</span>
                  <strong className={modalStyle.modalStatusValue}>
                    {status || "--"}
                  </strong>
                </div>
              </div>
              <p className={modalStyle.modalPriceNote}>
                Price shown for reference only.
              </p>
            </div>

            <div className={modalStyle.infoBox}>
              <div className={modalStyle.infoContent}>
                <div className={modalStyle.infoContentHeader}>
                  <h4>CHARACTER</h4>
                </div>
                <div className={modalStyle.infoContentBody}>
                  <ul className={modalStyle.infoBulletList}>
                    {characterList.length > 0 ? (
                      characterList.slice(0, 2).map((item, index) => (
                        <li
                          className={modalStyle.infoBulletItem}
                          key={`${item}-${index}`}
                        >
                          {index === 0 ? `${item} temper` : item}
                        </li>
                      ))
                    ) : (
                      <li className={modalStyle.infoBulletItem}>--</li>
                    )}
                  </ul>
                </div>
              </div>
              <div className={modalStyle.infoContent}>
                <div className={modalStyle.infoContentHeader}>
                  <h4>Finish</h4>
                </div>
                <div className={modalStyle.infoContentBody}>
                  <ul className={modalStyle.infoBulletList}>
                    {featureList.length > 0 ? (
                      featureList.map((fin, i) => (
                        <li className={modalStyle.infoBulletItem} key={fin + i}>
                          {fin}
                        </li>
                      ))
                    ) : (
                      <li className={modalStyle.infoBulletItem}>--</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {renderSupportPanel()}

            {canEdit && (
              <div className={modalStyle.vendorToolsBox}>
                <div className={modalStyle.vendorToolsMeta}>
                  <div className={modalStyle.vendorToolsRow}>
                    <span className={modalStyle.vendorToolsLabel}>
                      View Count
                    </span>
                    <strong>{view_count || 0}</strong>
                  </div>
                  {editMeta ? (
                    <span className={modalStyle.vendorToolsSub}>
                      {editMeta}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={modalStyle.vendorToolsButton}
                  onClick={onEdit}
                >
                  {editIcon}
                  <span>{editLabel || `Edit ${title}`}</span>
                </button>
              </div>
            )}
          </div>

          <div
            className={`${modalStyle.dataContainerBottom} ${
              isPageMode ? modalStyle.pageDataContainerBottom : ""
            }`}
          >
            <button
              type="button"
              className={`${modalStyle.bottomButton} ${modalStyle.bottomButtonSecondary} ${
                !onCollectionAction ? modalStyle.actionDisabled : ""
              }`}
              onClick={onCollectionAction}
            >
              {collectionActive ? starFillIcon : starOutlineIcon}
              <h4>{collectionLabel || "Add to Collection"}</h4>
            </button>

            {!secondaryIsLoading ? (
              <button
                type="button"
                className={`${modalStyle.bottomButton} ${
                  !onSecondaryAction ? modalStyle.actionDisabled : ""
                }`}
                onClick={onSecondaryAction}
              >
                {downloadIcon}
                <h4>{secondaryLabel}</h4>
              </button>
            ) : (
              <div
                className={`${modalStyle.bottomButton} ${modalStyle.actionLoading}`}
              >
                {downloadIcon}
                <h4>{secondaryLoadingLabel || "Loading"}</h4>
              </div>
            )}

            <button
              type="button"
              className={`${modalStyle.bottomButton} ${modalStyle.bottomButtonLight} ${
                !onPrimaryAction ? modalStyle.actionDisabled : ""
              }`}
              onClick={onPrimaryAction}
            >
              <h4>{primaryLabel}</h4>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaterialDetailLayout;
